import { useContext, useEffect, useState } from "react";
import { Pagination, RenderName, TableHeader, TableRow, UserSideBar } from "../../components";
import { headersType } from "../../models";
import { ToastContext } from "../../contexts/ToastContext/ToastContext";
import { useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { searchAnnualPlanSubscribers, resetSubscribers } from "../../redux/slices/OfficeSlice";
import { User } from "../../models/user.model";
import { AuthPermission } from "../../services/auth.service";
import EditPatientProfile from "../../components/forms/EditPatientProfile/EditPatientProfile";
import { getSorting } from "../../utils";
import moment from "moment";
import { UserAnnualPlan } from "../../models/user-annual-plan.model";
import { UserService } from "../../services/user.service";

export const headers: headersType[] = [
  {
    title: "Patient Name",
    extraClasses: "w-full pl-0 break-all",
    sortKey: "user.lastName",
    isClickable: true,
  },
  {
    title: "Plan",
    extraClasses: "w-full hidden lg:block",
    sortKey: "officeAnnualPlan.annualPlanType.name",
    isClickable: true,
  },
  {
    title: "Payment Frequency",
    extraClasses: "w-200 hidden lg:block",
    sortKey: "officeAnnualPlan.annualPlanType.subscriptionType",
    isClickable: false,
  },
  {
    title: "Start Date",
    extraClasses: "w-120 hidden lg:block",
    sortKey: "startDate",
    isClickable: true,
  },
  {
    title: "Status",
    extraClasses: "w-120 hidden lg:block",
    sortKey: "userAnnualPlanState",
    isClickable: false,
  },
];

const Subscribers = () => {
  const { id } = useParams();
  const toast = useContext(ToastContext);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [sortMethods, setSortMethods] = useState<boolean[]>(headers.map(() => false));
  const [sortKeys] = useState(headers.map(head => head.sortKey));
  const [activeSortIndex, setActiveSortIndex] = useState<number>(0);
  const [showTooltip, setShowTooltip] = useState(-1);
  const [openEditPatient, setOpenEditPatient] = useState(false);
  const [patientToBeUpdated, setPatientToBeUpdated] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [extraClasses] = useState<string[]>(headers.map(head => head.extraClasses));
  const { subscribers, subscribersSortObj } = useSelector((state: RootState) => state.office);
  const { currentUser } = useSelector((state: RootState) => state.currentUser);

  const load = async (page = 0, size = 20, sortBy = "user.lastName,desc", term?: string) => {
    if (id)
      dispatch(
        searchAnnualPlanSubscribers({
          page,
          size,
          sort: sortBy,
          term,
          officeId: id,
        })
      );
  };
  const onPageChange = (e: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    load(newPage, subscribersSortObj.rowsPerPage, subscribersSortObj.sortedBy, subscribersSortObj.searchTerm);
  };

  const onRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    load(subscribersSortObj.pageNumber, parseInt(event.target.value, 10), subscribersSortObj.sortedBy, subscribersSortObj.searchTerm);
  };

  const handleTooltipClick = async (id: string | null) => {
    if (id) {
      try {
        const res = await UserService.getUser(id);
        setPatientToBeUpdated(res);
        setOpenEditPatient(true);
      } catch (e: any) {
        toast?.openToast();
      }
    } else toast?.openToast();
  };

  const handleSort = (index: number) => {
    const sortMethodString = getSorting(sortKeys, index, sortMethods);
    setSortMethods([...sortMethods.slice(0, index), !sortMethods[index], ...sortMethods.slice(index + 1)]);
    setActiveSortIndex(index);
    load(subscribersSortObj.pageNumber, subscribersSortObj.rowsPerPage, sortMethodString, subscribersSortObj.searchTerm);
  };
  const handleRowClick = (plan: UserAnnualPlan) => {
    navigate(`/offices/${id}/patients/${plan.user.userId}`);
  };
  useEffect(() => {
    dispatch(resetSubscribers()); // Clear stale data
    load(subscribersSortObj.pageNumber, subscribersSortObj.rowsPerPage, subscribersSortObj.sortedBy);
  }, []);

  const headerIconAsc = <p> ↑</p>;
  const headerIconDesc = <p> ↓</p>;
  const sortIcons = sortMethods.map((item, index) => (activeSortIndex === index ? (item ? headerIconAsc : headerIconDesc) : null));
  const tooltipOptions = currentUser?.permissions.includes(AuthPermission.User) ? ["Edit Patient"] : ["View Patient"];

  return (
    <div className="w-full relative">
      {subscribers?.length ? (
        <>
          <div className="flex justify-between items-center border-b border-docnote-blue-light border-opacity-50">
            {headers.map((head, index) => (
              <TableHeader
                title={head.title}
                index={index}
                key={index}
                sortIcon={sortIcons[index]}
                handleSort={handleSort}
                extraClassesHeader={head.extraClasses}
                isClickable={head.isClickable}
              />
            ))}
            <div className="w-40 p-5 py-3">
              <span className="text-base text-docsigna-purple-dark inline-block w-5"></span>
            </div>
          </div>
          {subscribers.map((rowData: UserAnnualPlan, index) => {
            const payment = rowData?.officeAnnualPlan.annualPlanType.subscriptionType;
            const status = rowData?.userAnnualPlanState;
            const annualPlanType = rowData.officeAnnualPlan.annualPlanType.name;
            const formattedPlanType = annualPlanType
              ? annualPlanType === "Single"
                ? "Individual Plan"
                : annualPlanType === "Senior"
                ? "Senior Individual Plan"
                : `${annualPlanType} Plan`
              : "-";
            const formattedStatus = status ? status.charAt(0).toUpperCase() + status?.slice(1) : "-";
            const formattedPayment = payment ? payment.charAt(0).toUpperCase() + payment?.slice(1) : "-";
            const sortedData = {
              name: <RenderName title={`${rowData.user.firstName} ${rowData.user.lastName}`} subTitle={""} />,
              plan: formattedPlanType,
              paymentFrequency: formattedPayment,
              startDate: moment(rowData?.startDate).format("ll"),
              Status: formattedStatus,
            };
            return (
              <TableRow
                data={Object.values(sortedData)}
                key={index}
                id={rowData.user.userId}
                tooltipVisible={showTooltip === index}
                extraClassesRow={extraClasses}
                handleOpenTooltip={() => setShowTooltip(index)}
                handleCloseTooltip={() => setShowTooltip(-1)}
                tooltipOptions={tooltipOptions}
                handleTooltipClick={type => handleTooltipClick(rowData.user.userId || null)}
                onRowClick={() => handleRowClick(rowData)}
              />
            );
          })}
          <Pagination
            colSpan={10}
            count={subscribersSortObj?.totalElements ?? 0}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            page={subscribersSortObj.pageNumber}
            rowsPerPage={subscribersSortObj.rowsPerPage}
          />
        </>
      ) : (
        <p>You have no annual plan subscribers</p>
      )}
      <UserSideBar
        onClickOutside={() => {
          !isLoading ? setPatientToBeUpdated(null) : null;
        }}
        title={`${patientToBeUpdated?.firstName} ${patientToBeUpdated?.lastName ?? ""}`}
        visible={openEditPatient && !!patientToBeUpdated}
      >
        <EditPatientProfile
          userData={patientToBeUpdated ?? undefined}
          isEditable={currentUser?.permissions.includes(AuthPermission.User)}
          onClickCancel={val => {
            setPatientToBeUpdated(null);
            if (val) {
              load(subscribersSortObj.pageNumber, subscribersSortObj.rowsPerPage, subscribersSortObj.sortedBy, subscribersSortObj.searchTerm);
            }
          }}
          setIsLoading={setIsLoading}
          isLoading={isLoading}
        />
      </UserSideBar>
    </div>
  );
};

export default Subscribers;
